import { useEffect, useState } from "react";

import classNames from "classnames";

import { useMessageBus } from "@jti/fe-message-bus";

import { BreadcrumbItem } from "./BreadcrumbItem/BreadcrumbItem";
import { BreadcrumbsJsonLD } from "./SchemaData/BreadcrumbsJsonLD";

import styles from "./Breadcrumbs.module.scss";

export type BreadcrumbsList = {
  title: string;
  url: string;
  fullUrl?: string;
}[];

export type BreadcrumbsProps = {
  list: BreadcrumbsList;
  useHeadingTag?: boolean;
  isAutomaticallyExpanded?: boolean;
  className?: string;
  expandButtonLabel?: string;
};

export const Breadcrumbs = ({
  list,
  className,
  isAutomaticallyExpanded = false,
  useHeadingTag,
  expandButtonLabel,
}: BreadcrumbsProps) => {
  const messageBus = useMessageBus();
  const [lastBreadcrumbTitle, setLastBreadcrumbTitle] = useState(list.at(-1)?.title || "");
  const [isExpanded, setExpanded] = useState(isAutomaticallyExpanded);

  useEffect(() => {
    messageBus.subscribe({
      channel: "update.breadcrumb.name",
      callback: (data) => {
        setLastBreadcrumbTitle(data.payload.breadcrumbName);
      },
    });
  }, [messageBus]);

  if (list.length <= 1) return null;

  return (
    <>
      <ul className={classNames(styles.listContainer, className)}>
        {!isExpanded && (
          <li>
            <button
              type="button"
              aria-label={expandButtonLabel}
              aria-expanded={isExpanded}
              data-testid="breadcrumbsDots"
              className={styles.dots}
              onClick={() => setExpanded(true)}
            >
              <div className={styles.dot}></div>
              <div className={styles.dot}></div>
              <div className={styles.dot}></div>
            </button>
          </li>
        )}

        {list.map((breadcrumb, index) => {
          return (
            <BreadcrumbItem
              key={index}
              title={index === list.length - 1 ? lastBreadcrumbTitle : breadcrumb.title}
              url={breadcrumb.url}
              isHidden={!isExpanded && index < list.length - 1}
              hasPrev={index > 0}
              tag={useHeadingTag && index === list.length - 1 ? "h1" : "span"}
            />
          );
        })}
      </ul>
      <BreadcrumbsJsonLD breadcrumbsList={list} />
    </>
  );
};
